import * as React from 'react';

import { useSettingsLocation } from '@rematter/modules/settings/_state/settingsModule.state';
import {
  Button,
  Col,
  humanReadableAddress,
  Menu,
  Row,
  SearchField,
  Select,
  TinyTable,
} from '@rematter/ui-web';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  LucideComponent,
  LucideEllipsis,
  LucideLayers,
  LucideMapPin,
  LucidePlusCircle,
  LucideSignpost,
  LucideSquarePen,
  LucideTrash,
} from 'lucide-react';

import { LocationRecord } from '../../_graphql/queryLocationList';
import * as locationsState from '../../_state/locations.state';
import { SettingsContent } from '../_shared/SettingsContent';

import { LocationCreationDialog } from './LocationCreationDialog';

type LocationRowMenuProps = { row: LocationRecord };

const LocationRowMenu = (props: LocationRowMenuProps) => {
  const [, setLocation] = useSettingsLocation();
  const deleteFacility = useSetAtom(locationsState.$deleteFacility);

  const handleDelete = async () => {
    try {
      await deleteFacility({ id: props.row.id });
    } catch (error) {
      console.error('Failed to delete facility:', error);
    }
  };

  return (
    <div className={'@LocationRowMenu contents'}>
      <Menu
        trigger={
          <Button
            className={'px-2'}
            size="small"
            color="neutral"
            variant="outline"
            icon={<LucideEllipsis />}
          />
        }
        items={[
          {
            label: 'Edit',
            icon: <LucideSquarePen />,
            onAction: () =>
              setLocation(`/locations/facilities/${props.row.id}/info`),
          },
          {
            label: 'Delete',
            icon: <LucideTrash />,
            className: 'text-failure',
            secondStep: ({ close }) => (
              <Col gap3>
                <div className="text-center text-sm text-neutral/70">
                  Are you sure?
                </div>
                <Row gap2>
                  <Button variant="outline" color="neutral" onPress={close}>
                    Cancel
                  </Button>
                  <Button
                    variant="outline"
                    color="failure"
                    icon={<LucideTrash />}
                    onPress={() => {
                      close();
                      handleDelete();
                    }}
                  >
                    Yes, delete
                  </Button>
                </Row>
              </Col>
            ),
          },
        ]}
      />
    </div>
  );
};

export const LocationList = () => {
  const [, setLocation] = useSettingsLocation();
  const locationList = useAtomValue(locationsState.$locationList);
  const [locationType, setLocationType] = useAtom(
    locationsState.$locationTypeFilter,
  );
  const [searchTerm, setSearchTerm] = useAtom(locationsState.$searchTerm.value);
  const locationTypeList = useAtomValue(locationsState.$locationTypeList);
  const setIsLocationCreationDialogOpen = useSetAtom(
    locationsState.$isLocationCreationDialogOpen,
  );
  const locationTypeOptions = React.useMemo(
    () => [
      { label: 'All types', key: 'all' },
      ...locationTypeList.map((type) => ({
        label: type,
        key: type,
      })),
    ],
    [locationTypeList],
  );

  return (
    <SettingsContent className="@LocationList h-full">
      <Col gap4 className="h-full">
        <Row gap2>
          <SearchField value={searchTerm} onChange={setSearchTerm} autoFocus />
          {locationTypeList.length > 0 && (
            <Select
              className={'w-[200px]'}
              value={locationType}
              options={locationTypeOptions}
              onChange={(value) => setLocationType(value as 'all' | string)}
            />
          )}
          <Button
            className={'ml-auto'}
            icon={<LucidePlusCircle />}
            onPress={() => setIsLocationCreationDialogOpen(true)}
          >
            Add Facility
          </Button>
        </Row>
        <LocationCreationDialog />
        <TinyTable
          rowKey={(row) => row.id}
          isLoading={locationList.isLoading}
          error={locationList.error}
          onRowClick={(row) =>
            setLocation(`/locations/facilities/${row.id}/info`)
          }
          columns={[
            {
              key: 'name',
              label: 'Location Name',
              icon: LucideSignpost,
              cell: (row) => <div className={'truncate'}>{row.name}</div>,
              width: 250,
            },
            {
              key: 'type',
              label: 'Type',
              icon: LucideLayers,
              width: 150,
              cell: (row) => row.type,
            },
            {
              key: 'group',
              label: 'Group',
              icon: LucideComponent,
              width: 200,
              cell: (row) => (
                <div className={'truncate'}>
                  {row.facilityGroup?.name ?? '–'}
                </div>
              ),
            },
            {
              key: 'address',
              label: 'Address',
              icon: LucideMapPin,
              // width: 400,
              cell: (row) => (
                <div className="flex w-full">
                  <div className={'w-0 grow truncate'}>
                    {row.address
                      ? humanReadableAddress({
                          streetAddress: row.address.streetAddress,
                          city: row.address.city,
                          state: row.address.state,
                          postalCode: row.address.postalCode,
                        })
                      : ''}
                  </div>
                </div>
              ),
            },
            {
              key: 'actions',
              label: null,
              preventRowClick: true,
              width: 'max-content',
              cell: (row) => <LocationRowMenu row={row} />,
            },
          ]}
          data={locationList.data ?? []}
        />
      </Col>
    </SettingsContent>
  );
};
